
import TimeKeeper from '../engine/TimeKeeper';
import _cloneDeep from 'lodash/cloneDeep';
import Attack from './Attacks';
import AircraftModel from '../aircraft/AircraftModel';
import AttackController from './AttackController';
import { ATTACK_SETTING_NAMES } from '../constants/gameAttackConstants';

class NonMovingAttack extends Attack {
    constructor(startTimestamp) {
        super(startTimestamp);

        /**
         * Flag to indicate if the aircraft has run non-moving attack
         * 
         * Part of NON-MOVING attack
         * 
         * @for AircraftModel
         * @property hasRunNonMoving
         * @type {boolean}
         * @default false
         * @private
         */
        this.hasRunNonMoving = false;

        /**
         * Flag to indicate if the aircraft should stand still
         * 
         * Part of NON-MOVING attack
         * 
         * @for AircraftModel
         * @property shallIStandStill
         * @type {boolean}
         * @default false
         * @private
         */
        this.shallIStandStill = false;

        /**
         * For Non-moving attack, time time the aircraft will stand still in seconds
         * 
         * @for AircraftModel
         * @property standStillTime
         * @type {number}
         * @default 0
         * @private
         */
        this.standStillTime = 0;
        
        /**
         * Indicate when the aircraft started to stand still
         * 
         * Part of non-moving attack
         * 
         * @for AircraftModel
         * @property startNonMovingTime
         * @type {number}
         * @default 0
         * @private
         */
        this.startNonMovingTime = 0;

        /**
         * Minimum time the aircraft should stand still
         * 
         * Part of non-moving attack
         * 
         * @for AircraftModel
         * @property minStandStillTime
         * @type {number}
         * @default 3
         * @private
         */
        this.minStandStillTime = AttackController.attackOptions.getAttackOptionByName(ATTACK_SETTING_NAMES.NON_MOVING_MIN_TIME);

        /**
         * Maximum time the aircraft should stand still
         * 
         * Part of non-moving attack
         * 
         * @for AircraftModel
         * @property maxStandStillTime
         * @type {number}
         * @default 15
         * @private
         */
        this.maxStandStillTime = AttackController.attackOptions.getAttackOptionByName(ATTACK_SETTING_NAMES.NON_MOVING_MAX_TIME);

        /**
         * Copy of the aircraft when it is not moving
         * 
         * Part of non-moving attack
         * 
         * @for AircraftModel
         * @property nonStandingCopy
         * @type {AircraftModel}
         * @default null
         * @private
         */
        this.nonStandingCopy = null;

        this.init();
    }

    init() {
        super.init();
    }

    /**
     * @param {AircraftModel} aircraft 
     */
    update(aircraft) {
        if (!aircraft.isVisible()) {
            return;
        }
        this.updateMinAndMaxStandStillTime();
        if (TimeKeeper.accumulatedDeltaTime - this.startNonMovingTime >= this.standStillTime) {
            if (!this.hasRunNonMoving) {
                this.standStillTime = this._calculateStandStillTime();
                this.startNonMovingTime = TimeKeeper.accumulatedDeltaTime;

                this.nonStandingCopy = _cloneDeep(aircraft);

                // console.log(aircraft.callsign + " is non-moving");

                this.shallIStandStill = true;
            } else {
                // console.log(aircraft.callsign + " is moving again");
                this.standStillTime = this._calculateStandStillTime();
                this.startNonMovingTime = TimeKeeper.accumulatedDeltaTime;

                this.nonStandingCopy = null;

                this.shallIStandStill = false;
            }
            this.hasRunNonMoving = !this.hasRunNonMoving;
        }
    }

    /**
     * Reset the non-moving attack data
     * 
     * @for NonMovingAttack
     * @method resetAttackData
     */
    end(endTimestamp) {
        super.end(endTimestamp);
    }

    /**
     * Set the minimum and maximum time the aircraft should stand still
     * 
     * @for NonMovingAttack
     * @method setStandStillTime
     * @param {number} minStandStillTime
     * @param {number} maxStandStillTime
     * @throws {Error} Stand still time cannot be negative
     * @throws {Error} Minimum stand still time cannot be greater than maximum stand still time
     */
    setStandStillTime(minStandStillTime, maxStandStillTime) {
        if (minStandStillTime < 0 || maxStandStillTime < 0) {
            throw new Error('Stand still time cannot be negative');
        }
        if (minStandStillTime > maxStandStillTime) {
            throw new Error('Minimum stand still time cannot be greater than maximum stand still time');
        }

        this.minStandStillTime = minStandStillTime;
        this.maxStandStillTime = maxStandStillTime;
    }

    /**
     * Get the non-standing copy of the aircraft
     * 
     * @for NonMovingAttack
     * @method getNonStandingCopy
     * @return {AircraftModel}
     */
    getNonStandingCopy() {
        return this.nonStandingCopy;
    }

    /**
     * Get the flag to indicate if the aircraft should stand still
     * 
     * @for NonMovingAttack
     * @method getShallIStandStill
     * @returns {boolean}
     */
    getShallIStandStill() {
        return this.shallIStandStill;
    }

    /**
     * Calculate the time the aircraft should stand still
     * 
     * @for NonMovingAttack
     * @method _calculateStandStillTime
     * @return {number}
     * @private
     */
    _calculateStandStillTime() {
        return Math.floor(Math.random() * (this.maxStandStillTime - this.minStandStillTime + 1)) + this.minStandStillTime;
    }

    /**
     * Updates the min and max non-moving time if the settings has changed.
     * 
     * @for NonMovingAttack
     * @method updateMinAndMaxStandStillTime
     * @private
     */
    updateMinAndMaxStandStillTime() {
        const newMinStandStillTime = AttackController.attackOptions.getAttackOptionByName(ATTACK_SETTING_NAMES.NON_MOVING_MIN_TIME);
        const newMaxStandStillTime = AttackController.attackOptions.getAttackOptionByName(ATTACK_SETTING_NAMES.NON_MOVING_MAX_TIME);

        if (newMinStandStillTime !== this.minStandStillTime || newMaxStandStillTime !== this.maxStandStillTime) {
            this.setStandStillTime(newMinStandStillTime, newMaxStandStillTime);
        }
    }
}

export default NonMovingAttack;